"use client"

import * as React from "react"
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { ReactQueryDevtools } from "@tanstack/react-query-devtools"
import { Toaster } from "./components/ui/toaster"
import { AuthProvider } from "./contexts/AuthContext"
import { ThemeProvider } from "./contexts/ThemeContext"
import { ProtectedRoute } from "./components/ProtectedRoute"
import { LoadingSpinner } from "./components/LoadingSpinner"
import { ErrorBoundary } from "./components/ErrorBoundary"
import { useAuth } from "./contexts/AuthContext"
import { useToast } from "./hooks/use-toast"
import { setGlobalToast } from "./services/api"
import SSEClient from "./services/sse"
import { API_BASE_URL } from "./services/api"

// Pages
import LandingPage from "./pages/LandingPage"
import AboutPage from "./pages/AboutPage"
import ContactPage from "./pages/ContactPage"
import LoginPage from "./pages/LoginPage"
import RegisterPage from "./pages/RegisterPage"
import InvitePage from "./pages/InvitePage"
import DashboardPage from "./pages/DashboardPage"
import BoardPage from "./pages/BoardPage"
import BoardsPage from "./pages/BoardsPage"
import ProjectsPage from "./pages/ProjectsPage"
import TeamsPage from "./pages/TeamsPage"
import ProfilePage from "./pages/ProfilePage"
import SettingsPage from "./pages/SettingsPage"
import NotFoundPage from "./pages/NotFoundPage"
import logger from "./lib/logger"

// Styles
import "./App.css"

// Create a client for React Query with optimized settings
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      refetchOnMount: true,
      staleTime: 2 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
    },
    mutations: {
      retry: 1,
    },
  },
})


function ToastInitializer({ children }: { children: React.ReactNode }) {
  const toastHandler = useToast()

  React.useEffect(() => {
    // Set global toast after the Toaster component is mounted
    setGlobalToast(toastHandler)
  }, [toastHandler])

  return <>{children}</>
}

function SSEBridge() {
  const { user } = useAuth()

  React.useEffect(() => {
    if (!user) return

    const base = API_BASE_URL.replace(/\/$/, "")
    const sseUrl = base.match(/\/api\/?$/) ? `${base}/events/stream` : `${base}/api/events/stream`
    const sse = new SSEClient(sseUrl)

    sse.connect({
      // Teams
      "team.updated": () => {
        queryClient.invalidateQueries({ queryKey: ["teams"] })
        queryClient.invalidateQueries({ queryKey: ["user-teams", user.id] })
        queryClient.invalidateQueries({ queryKey: ["board-teams"] })
        queryClient.refetchQueries({ queryKey: ["board-teams"] })
        queryClient.invalidateQueries({ queryKey: ["board"] })
        queryClient.refetchQueries({ queryKey: ["board"] })
      },
      // Boards lifecycle affecting visibility of tasks in counts
      "board.archived": () => {
        queryClient.invalidateQueries({ queryKey: ["tasks", "due-today"] })
        queryClient.invalidateQueries({ queryKey: ["tasks", "due-soon"] })
        queryClient.invalidateQueries({ queryKey: ["boards"] })
        queryClient.invalidateQueries({ queryKey: ["profile", "activity"] })
      },
      "board.deleted": () => {
        queryClient.invalidateQueries({ queryKey: ["tasks", "due-today"] })
        queryClient.invalidateQueries({ queryKey: ["tasks", "due-soon"] })
        queryClient.invalidateQueries({ queryKey: ["boards"] })
        queryClient.invalidateQueries({ queryKey: ["profile", "activity"] })
      },
      "board.unarchived": () => {
        queryClient.invalidateQueries({ queryKey: ["tasks", "due-today"] })
        queryClient.invalidateQueries({ queryKey: ["tasks", "due-soon"] })
        queryClient.invalidateQueries({ queryKey: ["boards"] })
        queryClient.invalidateQueries({ queryKey: ["profile", "activity"] })
      },
      "board.restored": () => {
        queryClient.invalidateQueries({ queryKey: ["tasks", "due-today"] })
        queryClient.invalidateQueries({ queryKey: ["tasks", "due-soon"] })
        queryClient.invalidateQueries({ queryKey: ["boards"] })
        queryClient.invalidateQueries({ queryKey: ["profile", "activity"] })
      },
      "board.created": () => {
        queryClient.invalidateQueries({ queryKey: ["boards"] })
        queryClient.invalidateQueries({ queryKey: ["profile", "activity"] })
      },
      // Tasks lifecycle
      "task.created": (d: any) => {
        if (d?.boardId) queryClient.invalidateQueries({ queryKey: ["tasks", d.boardId] })
        queryClient.invalidateQueries({ queryKey: ["tasks", "due-today"] })
        queryClient.invalidateQueries({ queryKey: ["tasks", "due-soon"] })
        queryClient.invalidateQueries({ queryKey: ["profile", "activity"] })
      },
      "task.updated": (d: any) => {
        if (d?.boardId) queryClient.invalidateQueries({ queryKey: ["tasks", d.boardId] })
        queryClient.invalidateQueries({ queryKey: ["tasks", "due-today"] })
        queryClient.invalidateQueries({ queryKey: ["tasks", "due-soon"] })
        queryClient.invalidateQueries({ queryKey: ["profile", "activity"] })
      },
      "task.moved": (d: any) => {
        if (d?.boardId) queryClient.invalidateQueries({ queryKey: ["tasks", d.boardId] })
        queryClient.invalidateQueries({ queryKey: ["tasks", "due-today"] })
        queryClient.invalidateQueries({ queryKey: ["tasks", "due-soon"] })
        queryClient.invalidateQueries({ queryKey: ["profile", "activity"] })
      },
      "task.deleted": (d: any) => {
        if (d?.boardId) queryClient.invalidateQueries({ queryKey: ["tasks", d.boardId] })
        queryClient.invalidateQueries({ queryKey: ["tasks", "due-today"] })
        queryClient.invalidateQueries({ queryKey: ["tasks", "due-soon"] })
        queryClient.invalidateQueries({ queryKey: ["profile", "activity"] })
      },
      // Comments
      "comment.created": (d: any) => {
        if (d?.taskId) queryClient.invalidateQueries({ queryKey: ["comments", String(d.taskId)] })
      },
      "comment.deleted": (d: any) => {
        if (d?.taskId) queryClient.invalidateQueries({ queryKey: ["comments", String(d.taskId)] })
      },
      // Notifications
      "notification.created": () => {
        queryClient.invalidateQueries({ queryKey: ["notifications", "unread-count"] })
        queryClient.invalidateQueries({ queryKey: ["notifications", "list"] })
        try {
          const enabled = (window as any).localStorage
            ? JSON.parse(localStorage.getItem("notif_sound_enabled") || "true")
            : true
          const vol = (window as any).localStorage
            ? JSON.parse(localStorage.getItem("notif_sound_volume") || "70") / 100
            : 0.7
          if (enabled) {
            const audio = new Audio("/sounds/notify.mp3")
            audio.volume = vol
            audio.play().catch(() => {})
          }
        } catch {}
      },
    })

    return () => sse.close()
  }, [user])

  return null
}

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <AuthProvider>
            <Router>
              <div className="App min-h-screen bg-background text-foreground">
                <ToastInitializer>
                  <Toaster />
                  <SSEBridge />
                  <Routes>
                    {/* Marketing / public pages */}
                    <Route path="/" element={<LandingPage />} />
                    <Route path="/about" element={<AboutPage />} />
                    <Route path="/contact" element={<ContactPage />} />

                    {/* Auth routes — redirect to dashboard if already logged in */}
                    <Route
                      path="/login"
                      element={
                        <PublicRoute>
                          <LoginPage />
                        </PublicRoute>
                      }
                    />
                    <Route
                      path="/register"
                      element={
                        <PublicRoute>
                          <RegisterPage />
                        </PublicRoute>
                      }
                    />
                    <Route
                      path="/invite/:token"
                      element={<InvitePage />}
                    />
                    <Route
                      path="/dashboard"
                      element={
                        <ProtectedRoute>
                          <DashboardPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/boards"
                      element={
                        <ProtectedRoute>
                          <BoardsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/projects"
                      element={
                        <ProtectedRoute>
                          <ProjectsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/boards/:boardId"
                      element={
                        <ProtectedRoute>
                          <BoardPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/teams"
                      element={
                        <ProtectedRoute>
                          <TeamsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/teams/:teamId"
                      element={
                        <ProtectedRoute>
                          <TeamsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/profile"
                      element={
                        <ProtectedRoute>
                          <ProfilePage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/settings"
                      element={
                        <ProtectedRoute>
                          <SettingsPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* Catch all route - 404 */}
                    <Route path="*" element={<NotFoundPage />} />
                  </Routes>
                </ToastInitializer>
              </div>
            </Router>
          </AuthProvider>
        </ThemeProvider>

        {/* React Query Devtools - only in development */}
        {process.env.NODE_ENV === "development" && <ReactQueryDevtools initialIsOpen={false} />}
      </QueryClientProvider>
    </ErrorBoundary>
  )
}

// Public Route Component - redirects to dashboard if already authenticated
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth()

  logger.log("🔐 PublicRoute - isLoading:", isLoading, "user:", user);

  if (isLoading) {
    logger.log("⏳ PublicRoute showing loading spinner");
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    )
  }

  if (user) {
    logger.log("🚀 PublicRoute - User found, redirecting to dashboard:", user);
    return <Navigate to="/dashboard" replace />
  }

  logger.log("📄 PublicRoute - No user, showing public content");
  return <>{children}</>
}

export default App
